import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { getLPointShowValue, GetMoneyValue } from "../../../../Api/ApiCommon";
import { GetSubscriptionSetData } from "../../../../Api/ServiceDatas";
import { useApi } from "../../../../hooks/useApi";
import { payTypeString } from "../../../../pages/Pay/PayInfo";
import {
  reservedPayDropdown,
  reservedPaySearch,
} from "../../../../pages/Pay/ReservedPay";
import LineBtn from "../../../Button/LineBtn";
import Blank from "../../../DefaultStyles/Blank";
import LineBox from "../../../DefaultStyles/LineBox";
import Loader from "../../../DefaultStyles/Loader";
import TitleWithLine from "../../../DefaultStyles/TitleWithLine";
import Pagination from "../../../Table/Pagination";
import Table, { TableBody, TableBodyItem } from "../../../Table/Table";
import {
  IPayFail,
  payFailDropdownState,
  payFailSearchState,
} from "../../Pay/PayFail/CalculatePayFail";
import {
  payInfoDropdownState,
  payInfoSearchState,
} from "../../Pay/PayInfo/PayInfoFilter";
import UserDetailPayHistoryFilter from "./UserDetailPayHistory/UserDetailPayHistoryFilter";
import { PayReason } from "../../../../Api/Types";
import SpaceBetween from "../../../DefaultStyles/SpaceBetween";
import Flex from "../../../DefaultStyles/Flex";
import ToggleBtn from "../../../Button/ToggleBtn";
import { ChatType } from "../../Party/ChatInfo/ChatSearch/RecentChat/RecentChatHeader";
import { getSubsNameBySubsId3 } from "../../../../AdminApi/3.0_utils";

interface UserDetailPayHistoryProps {
  userId?: string;
}

interface IUserPayData {
  uid: string;
  type: string | number;
  servsubsid?: number;
  time: string;
  payday?: boolean;
  partyid?: number;
  reason?: string;
  err?: string;
  log: any;
  amount?: number;
}

interface IFailedData {
  err: string;
  time: string;
  type: string;
  partyid: number;
  reason: number;
  servsubsid: number;
  log?: any;
}

export default function UserDetailPayHistory({
  userId,
}: UserDetailPayHistoryProps) {
  const navigate = useNavigate();

  const [isNewVersion, setIsNewVersion] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [payData, setPayData] = useState<IUserPayData[]>([]);
  const [filter, setFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const setPayInfoDropdown = useSetRecoilState(payInfoDropdownState);
  const setPayInfoSearch = useSetRecoilState(payInfoSearchState);

  const setPayReservedDropdown = useSetRecoilState(reservedPayDropdown);
  const setPayReservedSearch = useSetRecoilState(reservedPaySearch);

  const setPayFailDropdown = useSetRecoilState(payFailDropdownState);
  const setPayFailSearch = useSetRecoilState(payFailSearchState);

  // 결제/적립/인출 정보
  const {
    response: payLogResponse,
    loading: payLogLoading,
    error: payLogError,
  } = useApi({
    method: "get",
    url: userId
      ? `${isNewVersion ? "pay/bill" : "pay"}?page=0&size=1000&uid=${userId}` +
        (filter?.endDate !== ""
          ? `&startdate=${filter?.startDate}&enddate=${filter?.endDate}`
          : ``)
      : undefined,
  });

  // 예약 결제
  const {
    response: reservedResponse,
    loading: reservedLoading,
    error: reservedError,
  } = useApi({
    method: "get",
    url: userId
      ? `pay/0?page=0&size=100&uid=${userId}` +
        (filter?.endDate !== ""
          ? `&startdate=${filter?.startDate}&enddate=${filter?.endDate}`
          : ``)
      : undefined,
  });

  // 결제 실패 리스트
  const {
    response: failResponse,
    loading: failLoading,
    error: failError,
  } = useApi({
    method: "get",
    url: userId
      ? `pay/1?page=0&size=100&uid=${userId}` +
        (filter?.endDate !== ""
          ? `&startdate=${filter?.startDate}&enddate=${filter?.endDate}`
          : ``)
      : undefined,
  });

  const payHistoryHeader = [
    "PID",
    "Subs_name",
    "적요",
    "금액",
    "결제 일시(예정)",
    "비고",
    "상세",
  ];

  useEffect(() => {
    if (
      payLogResponse?.status === 200 &&
      reservedResponse?.status === 200 &&
      failResponse?.status === 200
    ) {
      const reservedData: IFailedData[] = [];
      const failedData: IFailedData[] = [];

      reservedResponse?.data?.items?.forEach((d: IPayFail) => {
        d?.log?.forEach((a) => {
          let data = {} as IFailedData;

          data.log = a?.logdata ?? a?.log;
          data.err = d?.err;
          data.partyid = a?.partyid;
          data.reason = d?.reason;
          data.servsubsid = a?.subs;
          data.time = d?.time;
          data.type = "예약 결제";

          reservedData.push(data);
        });
      });

      failResponse?.data?.items?.forEach((d: IPayFail) => {
        d?.log?.forEach((a) => {
          let data = {} as IFailedData;

          data.log = a?.logdata ?? a?.log;
          data.err = d?.err;
          data.partyid = a?.partyid;
          data.reason = a?.reason;
          data.servsubsid = a?.subs;
          data.time = d?.time;
          data.type = "결제 실패";

          failedData.push(data);
        });
      });

      const combinedData = [
        ...payLogResponse?.data?.items,
        ...reservedData,
        ...failedData,
      ];

      const sortedCombinedData = combinedData?.sort((a: any, b: any) => {
        return dayjs(b.time).diff(dayjs(a.time), "seconds");
      });

      setTotalPage(sortedCombinedData?.length / 5 + 1);
      setPayData(sortedCombinedData);
    }
  }, [payLogResponse, reservedResponse, failResponse]);

  useEffect(() => {
    if (payLogError) {
      toast.error(
        payLogError?.data ?? "결제/적립/인출 내역 조회에 실패하였습니다."
      );
    }

    if (reservedError) {
      toast.error(
        reservedError?.data ?? "예약 결제 내역 조회에 실패하였습니다."
      );
    }

    if (failError) {
      toast.error(failError?.data ?? "결제 실패 내역 조회에 실패하였습니다.");
    }
  }, [payLogError, failError, reservedError]);

  return (
    <>
      <UserDetailPayHistoryFilter
        setFilter={setFilter}
        refreshPage={() => setPage(0)}
      />

      <LineBox>
        <>
          <SpaceBetween>
            <TitleWithLine title="정산 내역" />

            <Flex gapSize={8}>
              <ToggleBtn
                check={isNewVersion}
                checkEvent={() => setIsNewVersion((prev) => !prev)}
              />

              <ChatType isFirebase={!isNewVersion}>
                {isNewVersion ? "버전 3" : "버전 2"}
              </ChatType>
            </Flex>
          </SpaceBetween>

          <Blank blankHeight={24} />

          <Table tableHeadData={payHistoryHeader} tableMinWidth={965}>
            <>
              {payData
                ?.slice(page * 5, page * 5 + 5)
                ?.map((d, index: number) => {
                  const isPayLog = !isNaN(Number(d?.type));
                  const isReservedPay = d?.type === "예약 결제";
                  const isPayFail = d?.type === "결제 실패";

                  return (
                    <TableBody key={index}>
                      <TableBodyItem
                        clickable={d?.partyid !== 0 ? true : false}
                        onClick={() => {
                          if (d?.partyid !== 0) {
                            navigate(`/party/info/${d?.partyid}`);
                          }
                        }}
                      >
                        {d?.partyid !== 0 ? d?.partyid : "-"}
                      </TableBodyItem>
                      <TableBodyItem>
                        {d?.servsubsid !== 0 ? d?.servsubsid : "-"}
                        {d?.servsubsid
                          ? `_${getSubsNameBySubsId3(d?.servsubsid)}`
                          : ""}
                      </TableBodyItem>
                      <TableBodyItem>
                        {isPayLog
                          ? payTypeString(Number(d?.type), true)
                          : d?.type}
                      </TableBodyItem>
                      <TableBodyItem>
                        {GetMoneyValue(
                          d?.type === PayReason.Re_PartyFee_PartyEnd ||
                            d?.type === PayReason.Re_PartyFee_PayDay ||
                            d?.type === PayReason.Re_EndCompensation ||
                            d?.type === PayReason.Re_EndCompensation_Sset
                            ? d?.log?.amount
                            : d?.log?.amount - (d?.log?.point ?? 0)
                        )}
                      </TableBodyItem>
                      <TableBodyItem>{d?.time ?? "-"}</TableBodyItem>
                      <TableBodyItem>
                        {isPayLog && d?.payday
                          ? "정산일"
                          : isReservedPay
                          ? d?.reason !== ""
                            ? d?.reason
                            : "-"
                          : isPayFail
                          ? d?.err
                          : "-"}
                      </TableBodyItem>
                      <TableBodyItem>
                        <LineBtn
                          btnText="상세"
                          btnClickEvent={() => {
                            if (isPayLog) {
                              setPayInfoDropdown({
                                value: "uid",
                                label: "UID",
                              });
                              setPayInfoSearch(`${userId}`);
                            }

                            if (isReservedPay) {
                              setPayReservedDropdown({
                                value: "uid",
                                label: "UID",
                              });
                              setPayReservedSearch(`${userId}`);
                            }

                            if (isPayFail) {
                              setPayFailDropdown({
                                value: "uid",
                                label: "UID",
                              });
                              setPayFailSearch(`${userId}`);
                            }

                            navigate(
                              isPayLog
                                ? "/pay/info"
                                : isReservedPay
                                ? "/pay/reserve"
                                : `/pay/fail`,
                              {
                                state: { isNewVersion },
                              }
                            );
                          }}
                        />
                      </TableBodyItem>
                    </TableBody>
                  );
                })}
            </>
          </Table>

          <Pagination
            totalPage={totalPage}
            currentPage={page}
            pageClickEvent={(index) => setPage(index)}
          />

          {(payLogLoading || reservedLoading || failLoading) && <Loader />}
        </>
      </LineBox>
    </>
  );
}
